import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { when } from "@/lib/rides";
import { StatusBadge } from "@/components/RideStatus";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const setKeyOf = (r) => (r.series_id ? `series:${r.series_id}` : r.split_id ? `split:${r.split_id}` : null);

function Legs({ rides, picked, toggle, results }) {
  return (
    <div className="max-h-[45vh] space-y-1.5 overflow-y-auto" data-testid="ride-set-legs">
      {rides.map((r) => (
        <label key={r.id} className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 p-2 text-xs" data-testid={`ride-set-leg-${r.id}`}>
          <input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)} className="accent-[hsl(var(--primary))]" />
          <span className="min-w-0 flex-1">
            <span className="block font-semibold">{when(r.scheduled_at)}{r.split_leg ? ` · Leg ${r.split_leg}` : ""}</span>
            <span className="block truncate text-muted-foreground">{r.pickup.name} → {r.dropoff.name} · {r.driver_name || "No driver"}</span>
            {results[r.id] && <span className={`block ${results[r.id].ok ? "text-primary" : "text-destructive"}`}>{results[r.id].ok ? "Updated" : results[r.id].error}</span>}
          </span>
          <StatusBadge status={r.status} />
        </label>
      ))}
    </div>
  );
}

export function RideSetDialog({ setKey, open, onOpenChange }) {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["adminRideSet", setKey], queryFn: () => api.get(`/admin/ride-sets/${encodeURIComponent(setKey)}`).then((r) => r.data), enabled: open });
  const { data: drivers } = useQuery({ queryKey: ["approvedDrivers"], queryFn: () => api.get("/admin/drivers", { params: { status: "approved" } }).then((r) => r.data), enabled: open });
  const [picked, setPicked] = useState(new Set());
  const [driver, setDriver] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState({});
  const rides = (data?.rides || []).filter((r) => ["confirmed", "assigned"].includes(r.status));
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const apply = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/rides/assign-bulk", { ride_ids: [...picked], driver_id: driver || null });
      setResults(Object.fromEntries(res.results.map((r) => [r.id, r])));
      const bad = res.results.filter((r) => !r.ok).length;
      bad ? toast.warning(`${res.results.length - bad} updated, ${bad} had conflicts`) : toast.success(`${res.results.length} legs updated`);
      refetch();
      ["adminDashboard", "adminRides"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card sm:max-w-lg" data-testid="ride-set-dialog">
        <DialogHeader><DialogTitle className="font-display">Ride set · {data?.rider_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Pick one, several or all legs and assign them to a driver. Each driver only sees the legs assigned to them.
          {(data?.rides || []).some((r) => r.status === "pending") && ` ${(data.rides || []).filter((r) => r.status === "pending").length} pending legs are hidden until approved.`}</p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setPicked(new Set(rides.map((r) => r.id)))} data-testid="ride-set-select-all">Select all</Button>
          <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>Clear</Button>
        </div>
        <Legs rides={rides} picked={picked} toggle={toggle} results={results} />
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <select value={driver} onChange={(e) => setDriver(e.target.value)} className="h-10 rounded-md border bg-background px-2 text-sm" data-testid="ride-set-driver-select">
            <option value="">Unassign (no driver)</option>
            {drivers?.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <Button onClick={apply} disabled={busy || !picked.size} data-testid="ride-set-apply">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Apply to ${picked.size}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
